import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, TextInput, CopyAction, Icon, Link, tokens } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type {
  ShareToSocialArgs,
  SocialPlatform,
} from '@hotjar/webapp/features/Referrals/shateToSocial';
import { shareToSocial } from '@hotjar/webapp/features/Referrals/shateToSocial';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useHelpHotjarUrl } from '@hotjar/translations';

export interface HowItWorksProps {
  referralCode: string;
}

const ICON_WRAP_SIZE = '54px';

const IconWrap = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
  height: ${ICON_WRAP_SIZE};
  min-width: ${ICON_WRAP_SIZE};
  width: ${ICON_WRAP_SIZE};
  background-color: ${tokens.color.bg_info};
  border-radius: 50%;
  margin-right: ${tokens.spacer.x2};
  color: ${tokens.ALIAS_COLORS.primary_cta_alt};
`;

const Label = styled.p`
  ${tokens.text('body2')};
  color: ${tokens.color.text_light};
`;

const Li = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${tokens.spacer.x3};
`;

export const HowItWorks = ({ referralCode }: HowItWorksProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { click: trackClick } = useTrack();

  const onReferralLinkCopied = () =>
    trackClick('Referral Code Copied', 'Referrals Page > Hot It Works', {});

  const trackSocialClick = (eventName: string) => {
    trackClick(eventName, 'Referrals Page > Hot It Works', {});
  };

  const url = `https://hotjar.com/r/${referralCode}`;

  const onShareToSocialButtonClicked = ({ platform, description }: Partial<ShareToSocialArgs>) => {
    if (platform === 'twitter') {
      trackSocialClick('Share via Twitter');
    } else if (platform === 'facebook') {
      trackSocialClick('Share via Facebook');
    } else if (platform === 'linkedin') {
      trackSocialClick('Share via LinkedIn');
    }

    shareToSocial({
      url,
      platform,
      title: t('common:referrals.how-it-works.share-subject'),
      description: t(description || 'common:referrals.how-it-works.share-body'),
    } as ShareToSocialArgs);
  };

  const EMAIL_HREF = `mailto:?subject=${encodeURIComponent(
    t('common:referrals.how-it-works.email-subject') as string,
  )}&body=${encodeURIComponent(t('common:referrals.how-it-works.email-body') as string)} ${url}`;
  return (
    <>
      <ul
        css={css`
          list-style: none;
          padding: 0;
        `}
      >
        <Li>
          <IconWrap>
            <Icon icon="forward" size="l" />
          </IconWrap>
          <Label>{t('common:referrals.how-it-works.step1')}</Label>
        </Li>
        <Li>
          <IconWrap>
            <Icon icon="user_success" size="l" />
          </IconWrap>
          <Label>
            <Trans i18nKey="common:referrals.how-it-works.step2">
              Your contact uses it to create a <strong>paid account</strong>.
            </Trans>
          </Label>
        </Li>
        <Li>
          <IconWrap>
            <Icon icon="diamond" size="l" />
          </IconWrap>
          <Label>
            <Trans i18nKey="common:referrals.how-it-works.step3">
              We’ll give you <strong>$100</strong>. Score!
            </Trans>
          </Label>
        </Li>
      </ul>

      <section
        css={css`
          display: flex;
          align-items: center;
          margin-bottom: ${tokens.spacer.x3};
          margin-top: ${tokens.spacer.x4};
        `}
      >
        <div
          css={css`
            flex-grow: 1;
          `}
        >
          <TextInput
            data-testid="url-input"
            defaultValue={url}
            placeholder="Placeholder"
            isReadonly
            icon="hyperlink"
          />
        </div>
        <div
          css={css`
            width: ${tokens.spacer.x6};
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <CopyAction
            toCopy={url}
            description={t('common:referrals.how-it-works.copy.description')}
            onCopy={onReferralLinkCopied}
          />
        </div>
      </section>
      <section
        css={css`
          display: flex;
          flex-wrap: wrap;
          margin-bottom: ${tokens.spacer.x4};
        `}
      >
        {[
          { platform: 'linkedin' as SocialPlatform },
          {
            platform: 'twitter' as SocialPlatform,
            text: 'common:referrals.how-it-works.tweet',
            description: 'common:referrals.how-it-works.share-body-twitter',
          },
          { platform: 'facebook' as SocialPlatform },
        ].map(({ platform, text, description }) => (
          <Button
            key={platform}
            kind="ghost"
            size="s"
            icon={platform}
            data-testid={`social-share-${platform}`}
            onClick={() =>
              onShareToSocialButtonClicked({
                platform,
                description,
              })
            }
          >
            {t(text || 'common:referrals.how-it-works.share')}
          </Button>
        ))}
        <Button
          onClick={() => trackSocialClick('Share via Email')}
          size="s"
          as="a"
          kind="ghost"
          icon="email"
          href={EMAIL_HREF}
          data-testid="social-share-email"
        >
          {t('common:referrals.how-it-works.email')}
        </Button>
      </section>

      <Link
        isExternal
        kind="standalone"
        href={`${helpHotjarUrl}/articles/115011586467-The-Hotjar-Referral-Program`}
      >
        {t('common:referrals.how-it-works.terms-and-conditions')}
      </Link>
    </>
  );
};
